import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { initPushNotifications, unregisterPushNotifications } from '../utils/pushNotifications';

const AuthContext = createContext();

const TEAM_ROLE_PRIORITY = ['coach', 'assistant_coach', 'parent', 'player', 'family', 'spectator'];

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [hasTeam, setHasTeam] = useState(false);

  const normalizeUser = useCallback((rawUser, authToken = token) => {
    if (!rawUser) return null;

    const normalizedUser = {
      ...rawUser,
      token: authToken || rawUser.token,
      team_id: rawUser.team_id ?? rawUser.teamId ?? null,
      teamId: rawUser.teamId ?? rawUser.team_id ?? null,
      teamMemberships: Array.isArray(rawUser.teamMemberships) ? rawUser.teamMemberships : [],
      roles: Array.isArray(rawUser.roles)
        ? rawUser.roles
        : (rawUser.role ? [rawUser.role] : [])
    };

    if (!Array.isArray(normalizedUser.effectiveRoles)) {
      const effectiveRoles = new Set(normalizedUser.roles);

      normalizedUser.teamMemberships.forEach((membership) => {
        if (membership?.relationshipType) {
          effectiveRoles.add(membership.relationshipType);
        }
      });

      if (normalizedUser.hasLinkedPlayers) {
        effectiveRoles.add('parent');
      }

      normalizedUser.effectiveRoles = Array.from(effectiveRoles);
    }

    return normalizedUser;
  }, [token]);

  const deriveHasTeam = useCallback((userData) => {
    if (!userData) return false;
    if (Array.isArray(userData.teamMemberships) && userData.teamMemberships.length > 0) {
      return true;
    }
    return !!(userData.team_id ?? userData.teamId);
  }, []);

  // Set up axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common.Authorization;
    }
  }, [token]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get('/api/auth/me');
          const userData = normalizeUser(response.data.user, token);
          setUser(userData);
          setHasTeam(deriveHasTeam(userData));
        } catch (error) {
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token, normalizeUser, deriveHasTeam]);

  // Initialize push notifications (native only) once authenticated
  useEffect(() => {
    if (!user || !token) return;
    initPushNotifications();
  }, [user, token]);

  const login = useCallback(async (email, password) => {
    try {
      const response = await axios.post('/api/auth/login', { email, password });
      const { token: authToken, user: rawUser } = response.data;
      const normalizedUser = normalizeUser(rawUser, authToken);

      localStorage.setItem('token', authToken);
      setToken(authToken);
      setUser(normalizedUser);
      setHasTeam(deriveHasTeam(normalizedUser));

      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  }, [normalizeUser, deriveHasTeam]);

  const register = useCallback(async (userData) => {
    try {
      const response = await axios.post('/api/auth/register', userData);
      const { token: authToken, user: rawUser } = response.data;
      const normalizedUser = normalizeUser(rawUser, authToken);

      localStorage.setItem('token', authToken);
      setToken(authToken);
      setUser(normalizedUser);
      setHasTeam(deriveHasTeam(normalizedUser));

      toast.success('Registration successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  }, [normalizeUser, deriveHasTeam]);

  const logout = useCallback(() => {
    unregisterPushNotifications();
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setHasTeam(false);
    toast.success('Logged out successfully');

    setTimeout(() => {
      window.location.href = '/login';
    }, 1000);
  }, []);

  const updateProfile = useCallback(async (profileData) => {
    try {
      const config = profileData instanceof FormData ? undefined : {};
      const response = await axios.put('/api/auth/profile', profileData, config);
      const normalizedUser = normalizeUser(response.data, token);
      setUser(normalizedUser);
      setHasTeam(deriveHasTeam(normalizedUser));
      toast.success('Profile updated successfully!');
      return { success: true, data: normalizedUser };
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || 'Profile update failed';
      toast.error(message);
      return { success: false, error: message };
    }
  }, [normalizeUser, token, deriveHasTeam]);

  const joinTeam = useCallback((teamId) => {
    setHasTeam(true);
    setUser(prev => normalizeUser({ ...prev, team_id: teamId, teamId }, prev?.token || token));
  }, [normalizeUser, token]);

  const leaveTeam = useCallback(() => {
    setHasTeam(false);
    setUser(prev => normalizeUser({ ...prev, team_id: null, teamId: null }, prev?.token || token));
  }, [normalizeUser, token]);

  const changeRole = useCallback(async (newRole) => {
    try {
      const response = await axios.post('/api/auth/change-role', { role: newRole });
      const { token: authToken, user: rawUser } = response.data;
      const normalizedUser = normalizeUser(rawUser, authToken);

      localStorage.setItem('token', authToken);
      setToken(authToken);
      setUser(normalizedUser);
      setHasTeam(deriveHasTeam(normalizedUser));

      toast.success(`Role changed to ${newRole.replace('_', ' ')}`);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to change role';
      toast.error(message);
      return { success: false, error: message };
    }
  }, [normalizeUser, deriveHasTeam]);

  const getTeamRole = useCallback((teamId) => {
    if (!user?.teamMemberships) return null;

    const memberships = user.teamMemberships
      .filter(m => m.teamId === parseInt(teamId, 10))
      .sort((a, b) => TEAM_ROLE_PRIORITY.indexOf(a.relationshipType) - TEAM_ROLE_PRIORITY.indexOf(b.relationshipType));

    return memberships[0]?.relationshipType || null;
  }, [user]);

  const effectiveRoles = useMemo(() => user?.effectiveRoles || [], [user?.effectiveRoles]);
  const hasRole = useCallback((role) => effectiveRoles.includes(role), [effectiveRoles]);
  const isGlobalAdmin = !!user && ['admin', 'org_admin', 'app_manager'].includes(user.role);

  const isCoachOnTeam = useCallback((teamId = null) => {
    if (!user) return false;
    if (isGlobalAdmin) return true;

    if (teamId) {
      const teamRole = getTeamRole(teamId);
      return teamRole === 'coach' || teamRole === 'assistant_coach';
    }

    return user.teamMemberships?.some(m =>
      m.relationshipType === 'coach' || m.relationshipType === 'assistant_coach'
    ) || false;
  }, [user, getTeamRole, isGlobalAdmin]);

  const isParentOnTeam = useCallback((teamId = null) => {
    if (!user) return false;
    if (user.hasLinkedPlayers || hasRole('parent')) return true;

    if (teamId) {
      return getTeamRole(teamId) === 'parent';
    }

    return user.teamMemberships?.some(m => m.relationshipType === 'parent') || false;
  }, [user, getTeamRole, hasRole]);

  const isCoach = isCoachOnTeam();
  const isParent = !!user && (user.hasLinkedPlayers || hasRole('parent') || isParentOnTeam());
  const isPlayer = !!user && hasRole('player');
  const hasOrganizationAccess = !!user && ['org_admin', 'admin', 'app_manager'].includes(user.role);
  const canCreateTeams = !!user && (
    hasOrganizationAccess ||
    hasRole('coach') ||
    hasRole('assistant_coach') ||
    isCoachOnTeam()
  );

  const value = useMemo(() => ({
    user,
    loading,
    hasTeam,
    login,
    register,
    logout,
    updateProfile,
    joinTeam,
    leaveTeam,
    changeRole,
    isAuthenticated: !!user,
    effectiveRoles,
    isCoach,
    isParent,
    isPlayer,
    isGlobalAdmin,
    hasOrganizationAccess,
    canCreateTeams,
    getTeamRole,
    isCoachOnTeam,
    isParentOnTeam,
    teamMemberships: user?.teamMemberships || []
  }), [
    user,
    loading,
    hasTeam,
    login,
    register,
    logout,
    updateProfile,
    joinTeam,
    leaveTeam,
    changeRole,
    effectiveRoles,
    isCoach,
    isParent,
    isPlayer,
    isGlobalAdmin,
    hasOrganizationAccess,
    canCreateTeams,
    getTeamRole,
    isCoachOnTeam,
    isParentOnTeam
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
